import { useState } from "react";
import { Share2 } from "lucide-react";
import { track } from "@/lib/track";
import { buildScoreCardBlob } from "@/lib/scoreCard";

export default function ShareResultsCard({ score, pathway }) {
  const [shared, setShared] = useState(false);
  const [building, setBuilding] = useState(false);
  const label = pathway.shareLabel || "LEGACY READINESS";
  const checkupName = pathway.key === "complete" ? "Legacy Checkup™" : pathway.label;

  const downloadBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  const downloadPlainCard = () => {
    const a = document.createElement("a");
    a.href = "/og-image-vertical.jpg";
    a.download = "legacy-checkup-card.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const share = async () => {
    track("share_card_clicked", pathway.key);
    setBuilding(true);
    const url = window.location.origin + pathway.route;
    const text = `I scored ${score}% on the free ${checkupName} from Legacy Wave Consulting — Discover Today. Protect Tomorrow. Take yours:`;
    try {
      const blob = await buildScoreCardBlob(score, label);
      const file = new File([blob], "my-legacy-score-card.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, url });
        setShared(true);
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: `My ${checkupName} Score`, text, url });
        setShared(true);
        return;
      }
      downloadBlob(blob, "my-legacy-score-card.png");
    } catch (e) {
      if (e && e.name === "AbortError") return;
      downloadPlainCard();
    } finally {
      setBuilding(false);
    }
  };

  const download = async () => {
    setBuilding(true);
    try {
      const blob = await buildScoreCardBlob(score, label);
      downloadBlob(blob, "my-legacy-score-card.png");
    } catch {
      downloadPlainCard();
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div className="mt-8 bg-navy text-cream rounded-3xl p-7 sm:p-8 text-center hero-grain relative overflow-hidden" data-testid="share-results-card">
      <div className="absolute inset-0 gold-glow" />
      <div className="relative">
        <h2 className="font-serif text-2xl font-semibold">Know someone who should take this?</h2>
        <p className="mt-2 text-sm text-cream/75 leading-relaxed max-w-md mx-auto">
          Share a beautiful card with your score — or send the {checkupName} to family and friends. It takes just a couple of minutes.
        </p>
        <button
          onClick={share}
          disabled={building}
          data-testid="share-results-button"
          className="mt-5 w-full min-h-[56px] rounded-full btn-gold-luxe text-navy font-extrabold text-sm tracking-widest transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Share2 className="w-5 h-5" /> {building ? "PREPARING YOUR CARD…" : "SHARE MY SCORE CARD"}
        </button>
        <button
          onClick={download}
          disabled={building}
          data-testid="share-card-download-button"
          className="mt-3 text-xs font-semibold tracking-widest text-gold hover:text-cream transition-colors underline underline-offset-4 disabled:opacity-60"
        >
          {shared ? "SHARED — TAP TO SAVE YOUR CARD" : "OR SAVE MY SCORE CARD TO MY PHONE"}
        </button>
      </div>
    </div>
  );
}
